import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { endpoints } from "@/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/auth";
import { useAdmin } from "@/hooks/useAdmin";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Trophy, Coffee, AlertTriangle } from "lucide-react";
import RecipeParamsForm, { paramsFromDefaults, paramsToPayload } from "@/components/RecipeParamsForm";
import ImageUploader from "@/components/ImageUploader";

export default function AddRecipe() {
  const { t, lang, isRTL } = useI18n();
  const serifClass = isRTL ? "font-serif-ar" : "font-serif-en";
  const { user, loading } = useAuth();
  const isAdmin = useAdmin();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const challengeId = searchParams.get("challenge");

  const { data: methods = [] } = useQuery({ queryKey: ["methods"], queryFn: endpoints.methods });
  // When in challenge mode, fetch the active challenge to display its info (bean name, method lock).
  const { data: activeChallengeData } = useQuery({
    queryKey: ["active-challenge"],
    queryFn: endpoints.getActiveChallenge,
    enabled: !!challengeId,
  });
  const challenge = challengeId && activeChallengeData?.challenge?.id === challengeId
    ? activeChallengeData.challenge
    : null;
  const isChallengeMode = !!challenge;
  // Check existing submission to gate the form
  const { data: mySubData, isLoading: mySubLoading } = useQuery({
    queryKey: ["my-challenge-submission", challengeId, user?.user_id],
    queryFn: () => endpoints.getMyChallengeSubmission(challengeId),
    enabled: isChallengeMode && !!user,
  });
  const alreadySubmitted = mySubData?.submitted === true;

  const initialMethod = challenge?.method || "v60";
  const [form, setForm] = useState({
    title_en: "",
    title_ar: "",
    method: initialMethod,
    difficulty: "easy",
    image_url: "",
    ingredients_en: "",
    ingredients_ar: "",
    steps_en: "",
    steps_ar: "",
  });
  const [params, setParams] = useState(() => paramsFromDefaults(initialMethod));
  const [publishAsCurated, setPublishAsCurated] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // When method changes, refill params with that method's defaults
  const onMethodChange = (newMethod) => {
    if (isChallengeMode) return; // locked in challenge mode
    setForm((f) => ({ ...f, method: newMethod }));
    setParams(paramsFromDefaults(newMethod));
  };

  React.useEffect(() => {
    const next = challengeId ? `/recipes/new?challenge=${challengeId}` : "/recipes/new";
    if (!loading && !user) navigate(`/login?next=${encodeURIComponent(next)}`);
  }, [user, loading, navigate, challengeId]);

  // Sync form.method with the challenge's method when it loads (challenge mode locks the method).
  // We avoid setState-in-effect by deriving the effective method at render time.
  const effectiveMethod = isChallengeMode ? challenge.method : form.method;
  const effectiveParams = isChallengeMode && form.method !== challenge.method
    ? paramsFromDefaults(challenge.method)
    : params;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const split = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);
      const payload = {
        title_en: form.title_en,
        title_ar: form.title_ar,
        method: effectiveMethod,
        difficulty: form.difficulty,
        image_url: form.image_url || null,
        ingredients_en: split(form.ingredients_en),
        ingredients_ar: split(form.ingredients_ar),
        steps_en: split(form.steps_en),
        steps_ar: split(form.steps_ar),
        params: paramsToPayload(effectiveParams),
        is_community: isAdmin && publishAsCurated ? false : true,
      };
      if (isChallengeMode) {
        await endpoints.submitChallengeRecipe(challenge.id, payload);
        queryClient.invalidateQueries({ queryKey: ["community-feed"] });
        queryClient.invalidateQueries({ queryKey: ["recipes"] });
        toast.success(isRTL ? "وصفتك مُسجّلة في البطولة 🏆" : "Recipe entered in the tournament 🏆");
        navigate("/challenge");
      } else {
        await endpoints.createRecipe(payload);
        queryClient.invalidateQueries({ queryKey: ["community-feed"] });
        queryClient.invalidateQueries({ queryKey: ["recipes"] });
        toast.success(t.addRecipe.success);
        navigate("/recipes");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail === "already_submitted_tournament") {
        toast.error(isRTL ? "سجّلت وصفتك مسبقاً في هذه البطولة — محاولة واحدة فقط." : "You already submitted to this tournament — one attempt only.");
      } else if (detail === "challenge_not_active") {
        toast.error(isRTL ? "هذا التحدّي لم يعد نشطاً." : "Challenge no longer active.");
      } else if (typeof detail === "string" && detail.startsWith("method_must_be_")) {
        toast.error(isRTL ? `الطريقة المطلوبة: ${detail.replace("method_must_be_", "").toUpperCase()}` : `Method must be ${detail.replace("method_must_be_", "").toUpperCase()}`);
      } else {
        toast.error(t.addRecipe.error);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !user) {
    return <div className="max-w-2xl mx-auto p-10 text-[#5C544F]" data-testid="add-recipe-loading">{t.common.loading}</div>;
  }

  // Already submitted gate
  if (isChallengeMode && !mySubLoading && alreadySubmitted) {
    const r = mySubData?.recipe;
    return (
      <div className="max-w-2xl mx-auto px-6 lg:px-10 pt-12 lg:pt-20 pb-24" data-testid="already-submitted-page">
        <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-10 text-center">
          <Trophy className="w-12 h-12 mx-auto text-emerald-600 mb-4" />
          <h2 className={`${serifClass} text-2xl text-emerald-900 mb-3`}>
            {isRTL ? "سجّلت وصفتك مسبقاً ✅" : "You already submitted ✅"}
          </h2>
          <p className="text-sm text-emerald-800/80 mb-6 leading-relaxed">
            {isRTL
              ? "محاولة واحدة فقط طوال البطولة. تابع المنصّة لمعرفة موعد البثّ المباشر وإعلان الفائز."
              : "ONE attempt for the whole tournament. Watch the platform for the live tasting date and winner reveal."}
          </p>
          <div className="flex gap-3 justify-center flex-wrap">
            <Button onClick={() => navigate("/challenge")} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="back-to-challenge-btn">
              {isRTL ? "ارجع لصفحة التحدّي" : "Back to challenge"}
            </Button>
            {r && (
              <Button
                variant="outline"
                onClick={() => navigate(`/recipes?recipe=${encodeURIComponent(r.id)}`)}
                className="rounded-full"
                data-testid="view-submitted-recipe-btn"
              >
                {isRTL ? "افتح وصفتك" : "Open your recipe"}
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 pt-12 lg:pt-20 pb-24" data-testid="add-recipe-page">
      {isChallengeMode ? (
        <div className="rounded-2xl bg-gradient-to-br from-[#C86240]/10 to-amber-50 border-2 border-[#C86240]/30 p-6 mb-8" data-testid="challenge-banner">
          <div className="flex items-start gap-3">
            <Trophy className="w-7 h-7 text-[#C86240] flex-shrink-0 mt-1" />
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#C86240] font-bold mb-1">
                {isRTL ? "أنت تشارك في:" : "You are entering:"}
              </div>
              <h2 className={`${serifClass} text-2xl text-[#2A2421] mb-2`}>
                {isRTL ? challenge.title_ar : challenge.title_en}
              </h2>
              {(challenge.bean_name_ar || challenge.bean_name_en) && (
                <div className="flex items-center gap-2 text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
                  <Coffee className="w-4 h-4 text-amber-600" />
                  <span className="font-medium">
                    {isRTL ? "المحصول الرسمي: " : "Official bean: "}
                    {isRTL ? challenge.bean_name_ar : challenge.bean_name_en}
                  </span>
                </div>
              )}
              <div className="flex items-start gap-2 text-xs text-red-800 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <span className="leading-relaxed">
                  {isRTL
                    ? "محاولة واحدة فقط طوال البطولة. الطريقة مقفلة على V60. اكتب وصفتك الكاملة بدقّة."
                    : "ONE attempt for the whole tournament. Method locked to V60. Write your full recipe carefully."}
                </span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="text-xs uppercase tracking-[0.3em] text-[#C86240] mb-4">{isRTL ? "مشاركة" : "Share"}</div>
          <h1 className={`${serifClass} text-4xl lg:text-5xl text-[#2A2421] leading-[1.05]`}>{t.addRecipe.title}</h1>
          <p className="mt-4 text-[#5C544F] leading-relaxed">{t.addRecipe.lead}</p>
        </>
      )}

      <form onSubmit={submit} className="mt-12 space-y-6 soft-card rounded-2xl p-8" data-testid="add-recipe-form">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label={t.addRecipe.titleEn}>
            <Input value={form.title_en} onChange={set("title_en")} required data-testid="title-en-input" className="h-11 rounded-xl border-[#2A2421]/15" />
          </Field>
          <Field label={t.addRecipe.titleAr}>
            <Input value={form.title_ar} onChange={set("title_ar")} required dir="rtl" data-testid="title-ar-input" className="h-11 rounded-xl border-[#2A2421]/15" />
          </Field>
        </div>

        <div className="grid sm:grid-cols-3 gap-4">
          <Field label={t.addRecipe.method}>
            <Select value={effectiveMethod} onValueChange={onMethodChange} disabled={isChallengeMode}>
              <SelectTrigger className="h-11 rounded-xl border-[#2A2421]/15 disabled:opacity-100 disabled:cursor-not-allowed" data-testid="method-select-input"><SelectValue /></SelectTrigger>
              <SelectContent>
                {methods.map((m) => (
                  <SelectItem key={m.id} value={m.id}>{lang === "ar" ? m.name_ar : m.name_en}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isChallengeMode && (
              <p className="text-[10px] text-[#C86240] mt-1 font-medium">
                {isRTL ? "🔒 الطريقة مقفلة لهذا التحدّي" : "🔒 Locked for this challenge"}
              </p>
            )}
          </Field>
          <Field label={t.addRecipe.difficulty}>
            <Select value={form.difficulty} onValueChange={(v) => setForm({ ...form, difficulty: v })}>
              <SelectTrigger className="h-11 rounded-xl border-[#2A2421]/15" data-testid="difficulty-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="easy">{t.difficulty.easy}</SelectItem>
                <SelectItem value="medium">{t.difficulty.medium}</SelectItem>
                <SelectItem value="hard">{t.difficulty.hard}</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label={t.addRecipe.imageUrl}>
            <ImageUploader
              value={form.image_url}
              onChange={(url) => setForm((f) => ({ ...f, image_url: url }))}
              testid="add-image"
              aiPromptSuggestion={`${form.title_en || form.title_ar || "specialty coffee"} — ${effectiveMethod || "pour-over"} brew, warm magazine light, ceramic cup, shallow depth of field`}
            />
          </Field>
        </div>

        <RecipeParamsForm method={effectiveMethod} params={effectiveParams} onChange={setParams} testIdPrefix="params" />

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label={t.addRecipe.ingredientsEn}>
            <Textarea rows={5} value={form.ingredients_en} onChange={set("ingredients_en")} required data-testid="ingredients-en-input" className="rounded-xl border-[#2A2421]/15" />
          </Field>
          <Field label={t.addRecipe.ingredientsAr}>
            <Textarea rows={5} value={form.ingredients_ar} onChange={set("ingredients_ar")} required dir="rtl" data-testid="ingredients-ar-input" className="rounded-xl border-[#2A2421]/15" />
          </Field>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label={`${t.addRecipe.stepsEn} (${isRTL ? "اختياري" : "optional"})`}>
            <Textarea rows={6} value={form.steps_en} onChange={set("steps_en")} data-testid="steps-en-input" className="rounded-xl border-[#2A2421]/15" />
          </Field>
          <Field label={`${t.addRecipe.stepsAr} (${isRTL ? "اختياري" : "optional"})`}>
            <Textarea rows={6} value={form.steps_ar} onChange={set("steps_ar")} dir="rtl" data-testid="steps-ar-input" className="rounded-xl border-[#2A2421]/15" />
          </Field>
        </div>

        <Button
          type="submit"
          disabled={submitting}
          data-testid="submit-recipe-btn"
          className="w-full h-12 rounded-xl bg-[#C86240] hover:bg-[#b15637] text-white"
        >
          {submitting ? t.common.loading : (isChallengeMode ? (isRTL ? "أرسل وصفتي للبطولة" : "Submit my recipe to tournament") : t.addRecipe.submit)}
        </Button>

        {isAdmin && !isChallengeMode && (
          <div
            className="flex items-center justify-between gap-4 rounded-xl border border-[#C86240]/30 bg-[#C86240]/5 px-4 py-3"
            data-testid="admin-curated-toggle-row"
          >
            <div className={isRTL ? "text-right" : "text-left"}>
              <div className="text-sm font-semibold text-[#2A2421]">
                {isRTL ? "نشر في القسم المنتقى" : "Publish in Curated section"}
              </div>
              <div className="text-xs text-[#5C544F] mt-0.5">
                {isRTL
                  ? "خاص بالمشرف — تظهر الوصفة ضمن وصفات الموقع المنتقاة بدلاً من وصفات المجتمع."
                  : "Admin only — recipe appears in site's Curated list instead of the Community list."}
              </div>
            </div>
            <Switch
              checked={publishAsCurated}
              onCheckedChange={setPublishAsCurated}
              data-testid="publish-as-curated-switch"
            />
          </div>
        )}
      </form>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <Label className="text-xs uppercase tracking-[0.2em] text-[#5C544F]">{label}</Label>
    <div className="mt-2">{children}</div>
  </div>
);
